import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PaymentTypeService from '../../services/paymentType.service';
import { useTranslation } from '../../hooks/useTranslation';

const PaymentTypeList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const fetchPaymentTypes = async () => {
    setLoading(true);
    try {
      const response = await PaymentTypeService.getAll();
      setPaymentTypes(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching payment types:', error);
      showSnackbar(t('paymentTypes.errorLoading') + ': ' + (error.response?.data?.message || error.message), 'error');
      setPaymentTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await PaymentTypeService.delete(deleteDialog.id);
      showSnackbar(t('paymentTypes.deletedSuccess'));
      fetchPaymentTypes();
    } catch (error) {
      showSnackbar(t('paymentTypes.errorDeleting'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPaymentTypes = paymentTypes.filter(pt =>
    pt.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const frecuenciaLabel = (value) => {
    const map = { mensual: t('paymentTypes.monthly'), anual: t('paymentTypes.annual'), diario: t('paymentTypes.daily') };
    return map[value] || value;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('paymentTypes.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('paytypes.list') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/payment-types/deleted')}
                >
                  {t('paymentTypes.deletedPaymentTypes')}
                </Button>
              )}
              {hasPermission('paytypes.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/payment-types/new')}
                >
                  {t('paymentTypes.newPaymentType')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('paymentTypes.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('paymentTypes.amount')}</TableCell>
                  <TableCell>{t('paymentTypes.lateFee')}</TableCell>
                  <TableCell>{t('paymentTypes.lateFeeDays')}</TableCell>
                  <TableCell>{t('paymentTypes.installments')}</TableCell>
                  <TableCell>{t('paymentTypes.byDate')}</TableCell>
                  <TableCell>{t('paymentTypes.frequency')}</TableCell>
                  <TableCell>{t('paymentTypes.recurrence')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPaymentTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t('paymentTypes.noPaymentTypesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPaymentTypes.map((pt) => (
                    <TableRow key={pt.id} hover>
                      <TableCell>{pt.name}</TableCell>
                      <TableCell>{pt.monto}</TableCell>
                      <TableCell>{pt.mora}</TableCell>
                      <TableCell>{pt.dias_mora}</TableCell>
                      <TableCell>{pt.indefinite || pt.cuotas === 0 ? t('paymentTypes.indefiniteLabel') : pt.cuotas}</TableCell>
                      <TableCell>{pt.by_Date ? t('common.yes') : t('common.no')}</TableCell>
                      <TableCell>
                        <Chip label={frecuenciaLabel(pt.frecuencia)} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{pt.recurrencia}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('paytypes.create') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/payment-types/${pt.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('paytypes.create') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(pt.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('paymentTypes.deletePaymentType')}</DialogTitle>
        <DialogContent>
          <Typography>{t('paymentTypes.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentTypeList;
